// semeia-disable-reexport
import type Store from "@ember-data/store";
import type {
  CPSInformations,
  DirectAuthenticationStatus,
  DmpDocumentContent,
  FindDocumentsCriteria,
  INS,
  PatientVitaleInformation,
  WebPSURLs
} from "parog-web/cnam/types";
import type { AuthenticationContext } from "parog-web/contexts/authentication-context";
import type DmpDocumentMetadata from "parog-web/models/dmp-document-metadata";
import type DmpRetrievalStartedJournalEvent from "parog-web/models/dmp-retrieval-started-journal-event";
import {
  numberTriesForLastHourPerCpsId,
  numberTriesForLastTwentyFourHoursPerCpsId
} from "parog-web/utils/dmp-utils";

import type MedicModel from "parog-web/models/medic";
import type Patient from "parog-web/models/patient";

export abstract class DmpAdapter {
  declare authenticationContext: AuthenticationContext;
  declare currentLimitPerHour: number;
  LIMIT_PER_DAY = 200;
  LIMIT_PER_HOUR = 30;
  declare store: Store;
  UPPER_LIMIT_PER_HOUR = 80;

  get currentDailyRate(): number {
    const jes = this.store.peekAll(
      "dmp-retrieval-started-journal-event"
    ) as unknown as DmpRetrievalStartedJournalEvent[];
    return this.authenticationContext?.cpsInformations
      ? numberTriesForLastTwentyFourHoursPerCpsId(
          jes,
          this.authenticationContext.cpsInformations?.medicInternalId
        )
      : 0;
  }

  get currentHourlyRate(): number {
    const jes = this.store.peekAll(
      "dmp-retrieval-started-journal-event"
    ) as unknown as DmpRetrievalStartedJournalEvent[];
    return this.authenticationContext?.cpsInformations
      ? numberTriesForLastHourPerCpsId(
          jes,
          this.authenticationContext.cpsInformations?.medicInternalId
        )
      : 0;
  }

  get dailyRemainingRate(): number {
    return this.LIMIT_PER_DAY - this.currentDailyRate;
  }

  get hasExceededDailyRate(): boolean {
    return this.currentDailyRate >= this.LIMIT_PER_DAY;
  }

  get hasExceededHourlyRate(): boolean {
    return this.currentHourlyRate >= this.currentLimitPerHour;
  }

  get hourlyRemainingRate(): number {
    return this.currentLimitPerHour - this.currentHourlyRate;
  }

  get remainingRate(): number {
    return Math.min(this.hourlyRemainingRate, this.dailyRemainingRate);
  }

  constructor(public medic: MedicModel) {
    this.currentLimitPerHour = medic?.canUseDmpRetrievalUpperLimit
      ? this.UPPER_LIMIT_PER_HOUR
      : this.LIMIT_PER_HOUR;
  }

  abstract checkAvailability(): Promise<void>;

  abstract closeSession(): Promise<void>;

  abstract createConnector(secretConnection?: boolean): Promise<void>;

  abstract openSession(): Promise<void>;

  abstract readCpsCard(): Promise<CPSInformations>;

  // abstract observeCard: TaskForAsyncTaskFunction<
  //   unknown,
  //   (onCardNotAvailable: () => void) => Promise<void>
  // >;
  abstract readVitaleCard(): Promise<Array<PatientVitaleInformation>>;

  abstract shouldSetSecretConnection(patient: Patient): Promise<boolean>;

  abstract td02Exist(
    ins: INS,
    secretConnection: boolean
  ): Promise<DirectAuthenticationStatus>;

  abstract td03AddAuthorization(
    ins: INS,
    secretConnection: boolean
  ): Promise<void>;

  abstract td09GetWebPSURLs(
    ins: INS,
    secretConnection: boolean
  ): Promise<WebPSURLs>;

  abstract td31Find(
    ins: INS,
    criteria: FindDocumentsCriteria,
    secretConnection: boolean
  ): Promise<Array<DmpDocumentMetadata>>;

  abstract td32RetrieveContent(
    ins: INS,
    dmpDocumentMetadata: DmpDocumentMetadata,
    secretConnection: boolean
  ): Promise<DmpDocumentContent>;
}
